import React, { useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Text from '../../../../../shared/_TBComponents/Text';
import { showSuccessToast } from '../../../../../shared/utils/toastManager';
import {
  archiveUser,
  deleteArchivedUser,
  deleteUsers,
  resendActivationEmail,
  unarchiveUser,
} from '../../../ducks/navigatorActions';
import ConfirmModal from './ConfirmModal';

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #F7F7F7;
  border: 1px solid #EEEEEE;
  border-radius: 4px;
`;

const ManageAthletesModals = ({
  accountCode,
  athleteToDelete,
  athleteToResend,
  athleteToArchive,
  athleteToRestore,
  isArchived,
  onClearDelete,
  onClearResend,
  onClearArchive,
  onClearRestore,
  onRefetch,
  onRefetchArchived,
}) => {
  const dispatch = useDispatch();

  const isDeletingUsers = useSelector((state) => state.navigator.ui.isDeletingUsers);
  const isArchivingUser = useSelector((state) => state.navigator.ui.isArchivingUser);
  const isUnarchivingUser = useSelector((state) => state.navigator.ui.isUnarchivingUser);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );

  const [isSendingActivation, setIsSendingActivation] = useState(false);

  // Keep last selected athlete available while related modals or actions are rendering
  const resendRef = useRef(null);
  const deleteRef = useRef(null);
  const archiveRef = useRef(null);
  const restoreRef = useRef(null);

  if (athleteToResend) resendRef.current = athleteToResend;
  if (athleteToDelete) deleteRef.current = athleteToDelete;
  if (athleteToArchive) archiveRef.current = athleteToArchive;
  if (athleteToRestore) restoreRef.current = athleteToRestore;

  const displayResend = resendRef.current;
  const displayDelete = deleteRef.current;
  const displayArchive = archiveRef.current;
  const displayRestore = restoreRef.current;

  const handleResendConfirm = () => {
    // Registration emails can't be sent when usernames are enabled - the
    // confirm button is only actionable when that setting is off.
    if (usernamesEnabled) return;
    setIsSendingActivation(true);
    dispatch(resendActivationEmail(accountCode, athleteToResend.id, {
      onSuccess: () => {
        showSuccessToast('Activation email resent.');
        onClearResend();
        setIsSendingActivation(false);
      },
      onError: () => {
        setIsSendingActivation(false);
      },
    }));
  };

  return (
    <>
      <ConfirmModal
        isOpen={!!athleteToResend}
        onClose={onClearResend}
        onConfirm={handleResendConfirm}
        title='Resend Activation'
        message={usernamesEnabled
          ? 'Registration emails cannot be sent when usernames are enabled. To give this athlete access, reset their password and provide it to them directly.'
          : 'A new random password will be generated and sent to the selected athlete.'}
        cancelLabel={usernamesEnabled ? 'Close' : 'Cancel'}
        confirmLabel='Resend'
        confirmIcon='send'
        destructive={false}
        isLoading={isSendingActivation}
        hideConfirm={usernamesEnabled}
      >
        <InfoWrapper>
          <Text styledTextProps={{ fontSize: '14px' }}>
            {usernamesEnabled
              ? `${displayResend?.first} ${displayResend?.last}`
              : `Send to: ${displayResend?.first} ${displayResend?.last}`}
          </Text>
        </InfoWrapper>
      </ConfirmModal>

      <ConfirmModal
        isOpen={!!athleteToArchive}
        onClose={onClearArchive}
        onConfirm={() => dispatch(archiveUser(accountCode, athleteToArchive.id, {
          onSuccess: () => {
            showSuccessToast(`${athleteToArchive.first} ${athleteToArchive.last} has been archived.`);
            onRefetch();
            onRefetchArchived();
            onClearArchive();
          },
        }))}
        title='Archive Athlete'
        message={`Are you sure you want to archive ${displayArchive?.first} ${displayArchive?.last}? They will no longer appear in the active athletes list.`}
        confirmLabel='Archive'
        confirmIcon='archive'
        destructive={false}
        isLoading={isArchivingUser}
      />

      <ConfirmModal
        isOpen={!!athleteToRestore}
        onClose={onClearRestore}
        onConfirm={() => dispatch(unarchiveUser(accountCode, athleteToRestore.userId, {
          onSuccess: () => {
            showSuccessToast(`${athleteToRestore.first} ${athleteToRestore.last} has been restored.`);
            onRefetch();
            onClearRestore();
          },
        }))}
        title='Restore Athlete'
        message={`Are you sure you want to restore ${displayRestore?.first} ${displayRestore?.last}? They will be moved back to the active athletes list.`}
        confirmLabel='Restore'
        confirmIcon='restore'
        destructive={false}
        isLoading={isUnarchivingUser}
      />

      <ConfirmModal
        isOpen={!!athleteToDelete}
        onClose={onClearDelete}
        onConfirm={() => {
          const userId = athleteToDelete.userId ?? athleteToDelete.id;
          const action = isArchived
            ? deleteArchivedUser(accountCode, userId, {
              onSuccess: () => {
                showSuccessToast(`${athleteToDelete.first} ${athleteToDelete.last} has been deleted.`);
                onRefetchArchived();
                onClearDelete();
              },
            })
            : deleteUsers(accountCode, [userId], {
              onSuccess: () => {
                showSuccessToast(`${athleteToDelete.first} ${athleteToDelete.last} has been deleted.`);
                onRefetch();
                onClearDelete();
              },
            });
          dispatch(action);
        }}
        title='Delete Athlete'
        message={`Are you sure you want to delete ${displayDelete?.first} ${displayDelete?.last}? This action cannot be undone.`}
        isLoading={isDeletingUsers}
      />
    </>
  );
};

const athleteShape = PropTypes.shape({
  first: PropTypes.string,
  last: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

ManageAthletesModals.propTypes = {
  accountCode: PropTypes.string,
  athleteToDelete: athleteShape,
  athleteToResend: athleteShape,
  athleteToArchive: athleteShape,
  athleteToRestore: PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
  }),
  isArchived: PropTypes.bool,
  onClearDelete: PropTypes.func.isRequired,
  onClearResend: PropTypes.func.isRequired,
  onClearArchive: PropTypes.func.isRequired,
  onClearRestore: PropTypes.func.isRequired,
  onRefetch: PropTypes.func.isRequired,
  onRefetchArchived: PropTypes.func.isRequired,
};

ManageAthletesModals.defaultProps = {
  accountCode: null,
  athleteToDelete: null,
  athleteToResend: null,
  athleteToArchive: null,
  athleteToRestore: null,
  isArchived: false,
};

export default ManageAthletesModals;
